import { captureException } from '@sentry/nextjs'
import { I18NConfig } from 'next/dist/server/config-shared'
import { createClient } from '../../../prismicio'
import { Format, Utils } from 'common/utils'
import { getPosts, getTags } from 'common/cms-sdk'
import { NextPageWithLayout } from '../../../types/nextPageWithLayout'
import BlogPage, { BlogPostType } from '../index'
import { blogBreadcrumb } from 'components/util/containers/Breadcrumbs'
import Strings from 'project/localisation'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { NextSeo } from 'next-seo'
import { getPlans } from 'common/planData' // we need this to make JSX compile

type BlogPageType = {}

const PAGE_SIZE = 16

const TaggedBlogPage: NextPageWithLayout<BlogPostType> = ({
  plans,
  posts: initialPosts,
  tags,
  totalPages,
  currentPage = 1,
}) => {
  const router = useRouter()
  const tag = router.query.tag
    ? `${`${router.query.tag}`.replace(/-/g, ' ')}`
    : undefined
  return (
    <>
      <BlogPage
        noIndex
        noFollow={false}
        key={tag}
        plans={plans}
        breadcrumb={blogBreadcrumb().concat([
          {
            item: `https://www.hoxtonmix.com/blog/${router.query.tag}/`,
            name: `${Strings.blog} - ${tag}`,
            position: 3,
          },
        ])}
        posts={initialPosts}
        tag={tag}
        tags={tags}
        totalPages={totalPages}
        currentPage={currentPage}
      />
    </>
  )
}

const TAG_SPECIAL_CASES: Record<string, string> = {
  whatsapp: 'WhatsApp',
}

export async function getServerSideProps(
  context: I18NConfig & {
    locale?: string
    params: { tag: string }
    query: { page?: string }
  },
) {
  try {
    const client = createClient(context)
    const page = Math.max(1, parseInt(context.query?.page as string, 10) || 1)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const formattedTag = context.params.tag
      .split('-')
      .map(
        (v) =>
          TAG_SPECIAL_CASES[v.toLowerCase()] || Format.capitalizeFirstLetter(v),
      )
      .join(' ')
      .replace('And', '&')

    const posts = await getPosts(
      client,
      page,
      lang,
      PAGE_SIZE,
      undefined,
      formattedTag,
    )
    const tags = await getTags(client, lang)
    const plans = await getPlans()
    const totalPages = Math.ceil(posts.total_results_size / PAGE_SIZE)

    if (page > totalPages && page > 1) {
      return { notFound: true }
    }

    return {
      props: {
        plans,
        posts,
        tags,
        totalPages,
        currentPage: page,
      },
    }
  } catch (e) {
    captureException(e, { tags: { tag: context.params.tag } })
    return {
      props: {
        posts: null,
      },
    }
  }
}

export default TaggedBlogPage
